import { Html, Url } from '../../common';
import { BatchRecipientResultId, BatchStatusCode } from './data';

export interface FitwareMemberEngagerSuccessResponse<TResult> {
    result: TResult;
    meta: MetaSuccess;
}

export interface FitwareMemberEngagerFailureResponse {
    result: null;
    meta: MetaFailure;
}

export type FitwareMemberEngagerResponse<TResult> =
    | FitwareMemberEngagerFailureResponse
    | FitwareMemberEngagerSuccessResponse<TResult>;

export function isFitwareMemberEngagerSuccessResponse<TResult>(
    response: FitwareMemberEngagerResponse<TResult>,
): response is FitwareMemberEngagerSuccessResponse<TResult> {
    return (

        response &&

        response.meta &&
        response.meta.isValid &&
        response.meta.serviceStatus === 'Success' &&
        response.meta.responseCode.code === 'A01'
    );
}

export function isFitwareMemberEngagerFailureResponse(
    response: FitwareMemberEngagerFailureResponse,
): response is FitwareMemberEngagerFailureResponse {
    return (

        response &&

        response.meta &&
        (!response.meta.isValid ||
            response.meta.serviceStatus !== 'Success' ||
            response.meta.responseCode.code !== 'A01')
    );
}

/**
 * Example:
 * "meta": {
 *      "serviceStatus": "Success",
 *      "isValid": true,
 *      "responseCode": {
 *          "code": "A01",
 *          "description": "Accepted"
 *      }
 *  }
 */
export interface MetaSuccess {
    serviceStatus: 'Success';
    isValid: true;
    responseCode: {
        code: 'A01';
        description: 'Accepted';
    };
}

export interface MetaFailure {
    serviceStatus: string;
    isValid: boolean;
    responseCode: ResponseCode;
}

export type Meta = MetaFailure | MetaSuccess;

export interface ResponseCode {
    code: string;
    description: string;
}

export interface ArrayResult<TResult> {
    results: TResult[];
    totalRows: number;
    message: null;
}

/**
 * Example:
 * {
 *     "id": 5,
 *     "name": "Current Members - All Active Members",
 *     "code": "customlist.currentmembers",
 *     "typeID": 2,
 *     "areaID": 8,
 *     "accountID": 1,
 *     "sprocName": "MailingService.CustomListCurrentMembers",
 *     "sprocParamsQueryString": null,
 *     "engagerEnabled": true,
 *     "showInChildAccounts": true
 * },
 */
export interface FitwareMemberList {
    id: number;
    name: string;
    code: string;
    typeID: number;
    areaID: number;
    accountID: number;
    sprocName: string;
    sprocParamsQueryString: string | null;
    engagerEnabled: boolean;
    showInChildAccounts: boolean;
}

export interface ListTemplatesResult {
    masterTemplate: FitwareTemplateListEntry;
    accountTemplates: FitwareTemplateListEntry[];
}

export interface FitwareTemplateListEntry {
    accountID: number;
    canEditContents: boolean;
    code: string;
    createDate: string;
    createUser: string;
    hasPublishedContents: boolean;
    id: number;
    isActive: boolean;
    key: string;
    mailingServiceTemplateGroup: MailingService;
    mailingServiceType: MailingService;
    masterTemplateID: number | null;
    name: string;
    sortOrder: number | null;
    thumbnailPreviewImage: string;
    typeID: number;
    updateDate: string;
    updateUser: string;
}

export type GetTemplateQuery =
    | {
          AccountID: number;
          Code: string;
      }
    | {
          TemplateID: number;
      }
    | {
          TemplateKey: string;
      };

export interface FitwareTemplate {
    accountID: number;
    code: string;
    content: null;
    createDate: string;
    createUser: string;
    id: number;
    isActive: boolean;
    key: string;
    layoutID: number | null;
    mailingServiceTemplateLayout: MailingServiceTemplateLayout;
    masterTemplateID: number | null;
    name: string;
    publishedContent: Html;
    showInChildAccounts: boolean;
    subjectContent: string;
    templateGroupID: number;
    textContent: string | null;
    thumbnailPreviewImage: string;
    typeID: number;
    updateDate: string;
    updateUser: string;
}

export interface MailingServiceTemplateLayout {
    code: string | null;
    createDate: string;
    createUser: string | null;
    id: number;
    isActive: boolean;
    layoutHTML: Html | null;
    name: string | null;
    updateDate: string;
    updateUser: string | null;
}

export interface MailingService {
    id: number;
    name: string;
    code: string;
    isActive: boolean;
    createUser: string;
    createDate: string;
    updateUser: string;
    updateDate: string;
}

export interface SaveTemplateBody {
    AccountID: number;
    Content: string;
    IsActive: number;
    MasterTemplateID: number;
    Name: string;
    PublishedContent: string;
    ShowInChildAccounts: string;
    TemplateGroupID: number;
    TemplateID: number;
    TypeID: number;
    User: string;
}

export interface SaveTemplateSectionsWithContentsRequest {
    Name: string;
    AccountID: number;
    TypeID: number;
    TemplateGroupID?: number | null;
    MasterTemplateID?: number | null;
    Code: string;
    Key: string;
    // Content: Html | null; // Not used?
    PublishedContent: Html | null;
    ThumbnailPreviewImage: Url | null;
    ShowInChildAccounts: boolean;
    User: string;
    SubjectContent: string;
    TextContent?: string | null;
    MailingServiceType?: MailingServiceType;
    MailingServiceTemplateGroup?: null;
    MailingServiceTemplateSections: MailingServiceTemplateSection[];
    ID?: number | null;
    IsActive?: boolean;
    CreateUser?: string | null;
    CreateDate?: string;
    UpdateUser?: string | null;
    UpdateDate?: string;
}

export interface MailingServiceTemplateSection {
    TemplateID: number;
    MasterTemplateSectionID: number;
    IsMasterEditable: boolean;
    Content: string;
    Key: string;
    User: string;
    EnableDuplicate: boolean;
    SortOrder: null;
    MailingServiceTemplateSectionContents: MailingServiceTemplateSectionContent[];
    ID?: number;
    IsActive?: boolean;
    CreateUser?: string | null;
    CreateDate?: string;
    UpdateUser?: string | null;
    UpdateDate?: string;
}

export interface MailingServiceTemplateSectionContent {
    TemplateSectionID: number;
    MasterTemplateSectionContentID: number;
    TemplateSectionContentTypeID: number;
    Content: string;
    IsMasterEditable: boolean;
    Key: string;
    User: string;
    MailingServiceTemplateSectionContentType: null;
    ID?: number;
    IsActive?: boolean;
    CreateUser?: string | null;
    CreateDate?: string;
    UpdateUser?: string | null;
    UpdateDate?: string;
}

export interface MailingServiceType {
    ID: number;
    Name: string;
    Code: string;
}

export interface SaveTemplateSectionsWithContentsResult {
    mailingServiceTemplateGroup: MailingServiceTemplateGroupResponse;
    mailingServiceTemplateLayout: null;
    mailingServiceTemplateSections: MailingServiceTemplateSectionResponse[];
    id: number;
    name: string;
    code: string;
    key: string;
    accountID: number;
    typeID: number;
    templateGroupID: number;
    masterTemplateID: number;
    content: null;
    publishedContent: string;
    textContent: null;
    subjectContent: string;
    thumbnailPreviewImage: Url | null;
    sortOrder: null;
    showInChildAccounts: boolean;
    layoutID: null;
    isActive: boolean;
    createUser: string;
    createDate: string;
    updateUser: string;
    updateDate: string;
}

export interface MailingServiceTemplateGroupResponse {
    id: number;
    name: string;
    code: string;
    isActive: boolean;
    createUser: string;
    createDate: string;
    updateUser: string;
    updateDate: string;
}

export interface MailingServiceTemplateSectionResponse {
    id: number;
    templateID: number;
    masterTemplateSectionID: number;
    isMasterEditable: boolean;
    content: string;
    key: string;
    enableDuplicate: boolean;
    sortOrder: null;
    mailingServiceTemplateSectionContents: any[];
    isActive: boolean;
    createUser: string;
    createDate: string;
    updateUser: string;
    updateDate: string;
}

export interface UpdateTemplateRequest {
    AccountID: number | string;
    PublishedContent?: Html;
    TextContent?: string | null;
    SubjectContent?: string | null;
    ThumbnailPreviewImage?: Url | null;
    User: string;
    TemplateID: number | string;
}

export type UpdateTemplateResponse = FitwareTemplate;

export interface SaveBatchBody {
    AccountID: number;
    AuditUserID: number;
    BatchID: number;
    BatchStatusCode: string;
    FromEmail: string;
    FromName: string;
    IsActive?: boolean;
    Name: string;
    ScheduleDate?: string;
    TemplateID: number;
    TypeID: number;
}

export interface SaveBatchResult {
    id: number;
    scheduleDate: string;
    accountID: number;
    batchStatusID: number;
    typeID: number;
    templateID: number;
    isActive: boolean;
    createUserID: number;
    updateUserID: number;
    createDate: string;
    updateDate: string;
    name: string;
    fromName: string;
    fromEmail: string;
}

export interface SaveListForBatchBody {
    BatchID: number;
    ListID: number;
    User: string;
    IsActive: boolean;
}

export interface SaveListForBatchResult {
    id: number;
    batchID: number;
    listID: number;
    mailingServiceList: FitwareMemberList;
    meta: {
        serviceStatus: 'Success';
        isValid: true;
        responseCode: {
            code: null;
            description: null;
        }; // This result seems to have an extra, buggy "meta" object, with null code/description.
    };
}

export interface ListBatchesForAccountRequest {
    accountID: number;
}

export interface ListBatchesForAccountResult {
    batch: BatchDetails;
    statusName: string;
    typeName: string;
    templateName: string;
    culture: null;
    statusCode: BatchStatusCode;
}

export interface BatchDetails {
    id: number;
    scheduleDate: string | null;
    accountID: number;
    batchStatusID: number;
    typeID: number;
    templateID: number;
    isActive: boolean;
    createUserID: number;
    updateUserID: number;
    createDate: string;
    updateDate: string;
    name: string;
    fromName: string;
    fromEmail: string;
}

export interface GetListsForBatchRequest {
    batchID: number;
}

export interface GetListsForBatchResult {
    id: number;
    name: string;
    code: string;
    typeID: number;
    areaID: number;
    accountID: number;
    sprocName: string;
    sprocParamsQueryString: string | null;
    engagerEnabled: boolean;
    showInChildAccounts: boolean;
    isActive: boolean;
    createUser: string;
    createDate: string;
    updateUser: string;
    updateDate: string;
}

export interface ListRecipientsAndEventsForBatchRequest {
    accountID: number;
    batchID: number;
}

export interface ListRecipientsAndEventsForBatchResultEvent {
    id: BatchRecipientResultId;
    name: string;
    description: string;
    createDate: Date;
}

export interface ListRecipientsAndEventsForBatchResult {
    id: number;
    email: string;
    phone: null;
    resultID: BatchRecipientResultId;
    resultName: string;
    emailLogID: number | null;
    firstName: string;
    lastName: string;
    culture: null;
    events: ListRecipientsAndEventsForBatchResultEvent[];
}
